import React, { useMemo } from 'react'
import FormatCurrency from 'components/Common/FormatCurrency'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import { pluralizeToString } from 'lib/string/pluralize'
import TextLink from 'components/Luxkit/TextLink'
import { CalendarPickerMonthView } from 'selectors/offerPage/offerPageSelectors'

interface Props {
  duration: number;
  flightsOnly?: boolean;
  enableCalendarRecommendations: boolean;
  month: CalendarPickerMonthView;
  selected?: boolean;
  hasPromo: boolean;
}

enum DisplayVariant {
  ShowContentPackageAvailable,
  ShowFlightsNotYetAvailable,
  ShowRecommendations,
  ShowFlightOnlyNoCache,
}

function OfferCalendarFlightsMonthTile(props: Props) {
  const {
    month,
    duration,
    flightsOnly,
    enableCalendarRecommendations,
    selected,
    hasPromo,
  } = props

  const flightPriceAvailable = month.hotelTotal < month.packagedTotal

  const displayVariant = useMemo<DisplayVariant>(() => {
    if (month.hotelAvailable) {
      if (month.packagedAvailable) {
        if (flightsOnly && !month.flightCacheActive) {
          return DisplayVariant.ShowFlightOnlyNoCache
        }
        return DisplayVariant.ShowContentPackageAvailable
      } else if (!flightsOnly) {
        return DisplayVariant.ShowFlightsNotYetAvailable
      }
    }
    return DisplayVariant.ShowRecommendations
  }, [month.hotelAvailable, month.packagedAvailable, flightsOnly, month.flightCacheActive])

  const subTextColour = selected ? 'neutral-one' : 'neutral-three'

  return (<div>
    {displayVariant === DisplayVariant.ShowFlightOnlyNoCache && (
      <Caption variant="medium" colour={subTextColour}>
        {pluralizeToString('night', duration)}
      </Caption>
    )}
    {displayVariant === DisplayVariant.ShowRecommendations && enableCalendarRecommendations && (
      <>
        <Caption variant="large" colour={subTextColour}>SOLD OUT</Caption>
        <TextLink size="small">See more great offers</TextLink>
      </>
    )}
    {
      displayVariant === DisplayVariant.ShowContentPackageAvailable && (<>
        <Caption variant="medium" colour={subTextColour}>
          {pluralizeToString('night', duration)} {flightPriceAvailable && '+ flights '}from
        </Caption>
        <div>
          <BodyText colour={ hasPromo ? 'highlight-secondary' : undefined } as="span" variant="large" weight="bold">
            <FormatCurrency value={month.packagedTotal} />
          </BodyText>{' '}
          <Caption as="span" variant="medium" colour={subTextColour}>/total*</Caption>
          {!flightPriceAvailable &&
            <Caption as="span" variant="medium" colour={subTextColour}>
              {' '} + flights
            </Caption>
        }
        </div>
      </>)
    }
    {
      displayVariant === DisplayVariant.ShowFlightsNotYetAvailable && (<>
        <Caption variant="medium" colour={subTextColour}>Flights not yet available</Caption>
        <TextLink size="small">View hotel only</TextLink>
      </>)
    }
  </div>
  )
}

export default React.memo(OfferCalendarFlightsMonthTile)
